import { Typography } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import { Identifier, Record, useNotify, useRefresh } from 'react-admin'
import { Button as CustomButton } from 'shared/components/Button/Button'
import { ConfirmDialog } from 'shared/components/Dialog/ConfirmDialog'

import { captureExceptionIfProduction } from 'helpers/utils'
import { DEFAULT_ERROR_STRING } from '../../helpers/constants'

interface BaseActionDetailButtonProps {
  record: Record
  actionRequest: (id: Identifier) => Promise<void>
  actionTitle: React.ReactNode
  actionText: React.ReactNode
  basePath?: string
  redirectTo?: (path: string) => void
  button_size?: 'small' | 'medium' | 'large'
  className?: string
  disabled?: boolean
  buttonStyle?: object
  variant?: 'contained' | 'text' | 'outlined' | undefined
  handleRequestStart?: () => void
  handleRequestEnd?: () => void
  useNewButton?: boolean
}

export const BaseActionDetailButton = ({
  record,
  actionRequest,
  actionTitle,
  actionText,
  basePath,
  redirectTo,
  button_size,
  className = '',
  disabled,
  buttonStyle,
  variant,
  handleRequestStart,
  handleRequestEnd,
  useNewButton,
}: BaseActionDetailButtonProps) => {
  const notify = useNotify()
  const refresh = useRefresh()
  const custom_button_size = button_size ? button_size : 'small'

  async function handleConfirm() {
    handleRequestStart?.()
    await actionRequest(record.id)
    handleRequestEnd?.()
    notify('Success!', { type: 'success' })
    if (!basePath || !redirectTo) {
      refresh()
    } else {
      redirectTo(basePath)
    }
  }

  function handleError(error: any) {
    handleRequestEnd?.()
    console.error(error)
    notify(`${DEFAULT_ERROR_STRING}: ${JSON.stringify(error?.response?.data || error)}`, {
      type: 'error',
    })
    captureExceptionIfProduction(error)
  }

  if (disabled && useNewButton) return null

  return (
    <div style={{ marginBottom: '20px', marginTop: '20px' }} className={className}>
      <ConfirmDialog
        handleConfirm={handleConfirm}
        handleError={handleError}
        dialogContent={
          <>
            <Typography variant="h4" align="center">
              {actionTitle}
            </Typography>
            <Typography align="center">{actionText}</Typography>
          </>
        }
        confirmLabel="Confirm"
        cancelLabel="Cancel"
      >
        {useNewButton ? (
          <CustomButton size="medium" variant="primary" isDisabled={disabled}>
            {actionTitle}
          </CustomButton>
        ) : (
          <Button
            size={custom_button_size}
            variant={variant ? variant : 'contained'}
            color="primary"
            disabled={disabled}
            style={buttonStyle}
          >
            {actionTitle}
          </Button>
        )}
      </ConfirmDialog>
    </div>
  )
}
